import React, { useEffect, useState } from 'react';
import { useNotificationStore } from '../../store/notificationStore';
import { 
  NotificationType, 
  NotificationChannel, 
  NotificationPreference,
  UpdateNotificationPreferenceRequest 
} from '../../types';
import { Button } from '../ui/Button';
import { clsx } from 'clsx';
import { 
  Bell, 
  BellOff, 
  Smartphone, 
  Mail, 
  MessageSquare, 
  Monitor,
  Clock,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const notificationTypeLabels: Record<NotificationType, { label: string; description: string; icon: React.ReactNode }> = {
  [NotificationType.HabitReminder]: {
    label: 'Habit Reminders',
    description: 'Get reminded when it\'s time to complete your habits',
    icon: <Bell className="w-5 h-5" />
  },
  [NotificationType.StreakMilestone]: {
    label: 'Streak Milestones',
    description: 'Celebrate when you reach streak goals',
    icon: <CheckCircle className="w-5 h-5" />
  },
  [NotificationType.MissedHabit]: {
    label: 'Missed Habits',
    description: 'Get notified when you miss a habit',
    icon: <AlertCircle className="w-5 h-5" />
  },
  [NotificationType.WeeklyReport]: {
    label: 'Weekly Reports',
    description: 'Receive weekly progress summaries',
    icon: <Monitor className="w-5 h-5" />
  },
  [NotificationType.Achievement]: {
    label: 'Achievements',
    description: 'Get notified about new achievements',
    icon: <CheckCircle className="w-5 h-5" />
  },
  [NotificationType.StreakEndanger]: {
    label: 'Streak Warnings',
    description: 'Warning when your streak is at risk',
    icon: <AlertCircle className="w-5 h-5" />
  },
  [NotificationType.Motivation]: {
    label: 'Motivational Messages',
    description: 'Receive encouraging messages',
    icon: <Bell className="w-5 h-5" />
  }
};

const channelLabels: Record<NotificationChannel, { label: string; icon: React.ReactNode }> = {
  [NotificationChannel.InApp]: {
    label: 'In-App',
    icon: <Monitor className="w-4 h-4" />
  },
  [NotificationChannel.BrowserPush]: {
    label: 'Browser Push',
    icon: <Smartphone className="w-4 h-4" />
  },
  [NotificationChannel.Email]: {
    label: 'Email',
    icon: <Mail className="w-4 h-4" />
  },
  [NotificationChannel.SMS]: {
    label: 'SMS',
    icon: <MessageSquare className="w-4 h-4" />
  }
};

interface NotificationPreferenceCardProps {
  preference: NotificationPreference;
  onUpdate: (type: NotificationType, request: UpdateNotificationPreferenceRequest) => Promise<void>;
  onTest: (type: NotificationType) => Promise<void>;
  isLoading: boolean;
  browserPushSupported: boolean;
  browserPushPermission: NotificationPermission;
  onRequestPermission: () => Promise<void>;
}

const NotificationPreferenceCard: React.FC<NotificationPreferenceCardProps> = ({
  preference,
  onUpdate,
  onTest,
  isLoading,
  browserPushSupported,
  browserPushPermission,
  onRequestPermission
}) => {
  const [localPreference, setLocalPreference] = useState(preference);
  const [hasChanges, setHasChanges] = useState(false);

  const typeInfo = notificationTypeLabels[preference.type];

  useEffect(() => {
    setLocalPreference(preference);
    setHasChanges(false);
  }, [preference]);

  const handleToggle = () => {
    const newPreference = { ...localPreference, isEnabled: !localPreference.isEnabled };
    setLocalPreference(newPreference);
    setHasChanges(true);
  };

  const handleChannelChange = (channel: NotificationChannel) => {
    const newPreference = { ...localPreference, channel };
    setLocalPreference(newPreference);
    setHasChanges(true);
  };

  const handleTimeChange = (time: string) => {
    const newPreference = { ...localPreference, preferredTime: time };
    setLocalPreference(newPreference);
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onUpdate(preference.type, {
      isEnabled: localPreference.isEnabled,
      channel: localPreference.channel,
      preferredTime: localPreference.preferredTime,
      timeZone: localPreference.timeZone,
      settings: localPreference.settings
    });
    setHasChanges(false);
  };

  const needsBrowserPermission = localPreference.channel === NotificationChannel.BrowserPush && 
                                 browserPushPermission !== 'granted';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-primary-600 dark:text-primary-400">
            {typeInfo.icon}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {typeInfo.label}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {typeInfo.description}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleToggle}
            className={clsx(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              localPreference.isEnabled
                ? 'bg-primary-600'
                : 'bg-gray-200 dark:bg-gray-700'
            )}
          >
            <span
              className={clsx(
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                localPreference.isEnabled ? 'translate-x-6' : 'translate-x-1'
              )}
            />
          </button>
        </div>
      </div>

      {localPreference.isEnabled && (
        <div className="space-y-4">
          {/* Channel Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notification Method
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(NotificationChannel).filter(value => typeof value === 'number').map((channel) => {
                const channelValue = channel as NotificationChannel;
                const info = channelLabels[channelValue];
                const isDisabled = channelValue === NotificationChannel.BrowserPush && !browserPushSupported;
                
                return (
                  <button
                    key={channelValue}
                    onClick={() => !isDisabled && handleChannelChange(channelValue)}
                    disabled={isDisabled}
                    className={clsx(
                      'flex items-center space-x-2 p-3 rounded-lg border text-sm transition-colors',
                      localPreference.channel === channelValue
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500',
                      isDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {info.icon}
                    <span>{info.label}</span>
                  </button>
                );
              })}
            </div>
            
            {needsBrowserPermission && (
              <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-sm text-yellow-800 dark:text-yellow-200">
                    Browser notifications require permission.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onRequestPermission}
                    className="ml-auto"
                  >
                    Enable
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Time Selection for relevant notifications */}
          {(preference.type === NotificationType.HabitReminder || 
            preference.type === NotificationType.WeeklyReport) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Preferred Time
              </label>
              <input
                type="time"
                value={localPreference.preferredTime || '09:00'}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            {hasChanges && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={isLoading}
              >
                Save Changes
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const NotificationPreferences: React.FC = () => {
  const {
    preferences,
    isLoading,
    error,
    browserPushSupported,
    browserPushPermission,
    loadPreferences,
    updatePreference,
    sendTestNotification,
    requestBrowserPushPermission,
    checkBrowserPushSupport,
    clearError
  } = useNotificationStore();

  useEffect(() => {
    checkBrowserPushSupport();
    loadPreferences();
  }, [checkBrowserPushSupport, loadPreferences]);

  const handleUpdatePreference = async (type: NotificationType, request: UpdateNotificationPreferenceRequest) => {
    await updatePreference(type, request);
  };

  const handleTestNotification = async (type: NotificationType) => {
    await sendTestNotification(type);
  };

  const handleRequestPermission = async () => {
    await requestBrowserPushPermission();
  };

  if (isLoading && (!preferences || preferences.length === 0)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Notification Preferences
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Customize how and when you receive notifications
          </p>
        </div>
        
        {!browserPushSupported && (
          <div className="flex items-center space-x-2 text-sm text-yellow-600 dark:text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span>Browser notifications not supported</span>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={clearError}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-6">
        {preferences && preferences.map((preference) => (
          <NotificationPreferenceCard
            key={preference.type}
            preference={preference}
            onUpdate={handleUpdatePreference}
            onTest={handleTestNotification}
            isLoading={isLoading}
            browserPushSupported={browserPushSupported}
            browserPushPermission={browserPushPermission}
            onRequestPermission={handleRequestPermission}
          />
        ))}
      </div>

      {(!preferences || preferences.length === 0) && !isLoading && (
        <div className="text-center py-12">
          <BellOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No notification preferences found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Your notification preferences will appear here once they're set up.
          </p>
        </div>
      )}
    </div>
  );
}; 